import React, { Key } from "react";

import styled from "styled-components";

import { Strip } from "manager/components/app-summary/action-bar/DealBanner/styled";
import { useGetVero1Checklist } from "manager/hooks/api/vero1Queries";
import {
  RECOMMENDATION_LABELS,
  Vero1ChecklistStatus,
  Vero1Recommendation,
} from "shared/interfaces/api/vero1";

import { InternalOnlyChip } from "./InternalOnlyChip";
import { recommendationVerdictColor } from "./vero1BannerState";
import { useVero1Viewer } from "./vero1Viewer";


const Row = styled.details<{ $nested?: boolean }>`
  width: max-content;
  max-width: 100%;
  /* spec: 13px below the rationale paragraph when nested in its rule */
  margin-top: ${({ $nested }) => ($nested ? "13px" : "0")};

  & > summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
    padding: 9px 13px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 4px;
    cursor: pointer;
    list-style: none;
  }
  & > summary::-webkit-details-marker {
    display: none;
  }
  &[open] > summary {
    border-radius: 4px 4px 0 0;
  }
  &:not([open]) summary svg {
    transform: rotate(-90deg);
  }
  & .toggle-show {
    display: none;
  }
  &:not([open]) .toggle-hide {
    display: none;
  }
  &:not([open]) .toggle-show {
    display: inline;
  }
`;

const SummaryText = styled.span`
  font: 400 12px "Work Sans", sans-serif;
  color: #cfc9e4;
  /* squeezed columns: take the remaining line or wrap whole onto the next,
     rather than threading a sliver beside the chip */
  flex: 1 1 16ch;
  min-width: min(100%, 22ch);
`;

const ToggleHint = styled.span`
  font: 300 11px "Work Sans", sans-serif;
  color: #8f89b0;
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  padding-left: 9px;
  margin-left: auto;
`;

const Body = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 14px 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.03);
`;

const ColLabel = styled.div`
  font: 600 9.5px "Work Sans", sans-serif;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #8f89b0;
  margin-bottom: 6px;
`;

const PriorVerdict = styled.div<{ $color?: string | null }>`
  font: 600 15px "Work Sans", sans-serif;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $color }) => $color ?? "#C8C3DC"};
`;

const PriorSource = styled.div`
  font: 300 10.5px "Work Sans", sans-serif;
  color: #8f89b0;
  margin-top: 3px;
`;

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.12);
  flex: 0 0 auto;
`;

const Changed = styled.div`
  max-width: 62ch;
`;

const ChangedBody = styled.p`
  font: 300 12.5px/1.6 "Work Sans", sans-serif;
  color: #d7d3ea;
  margin: 0;
`;

const AdverseNote = styled.p`
  font: 300 12px/1.6 "Work Sans", sans-serif;
  color: #a9a3c9;
  margin: 0;
  ${ChangedBody} + & {
    margin-top: 9px;
  }
`;

const runDate = (ts: string | number | null): string | null => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export interface Vero1LumenAmendedRowProps {
  dealId: Key;
  vero1Active?: boolean;
  standalone?: boolean;
}

export const Vero1LumenAmendedRow = ({
  dealId,
  vero1Active,
  standalone,
}: Vero1LumenAmendedRowProps) => {
  const { canViewInternal } = useVero1Viewer();
  const { checklist } = useGetVero1Checklist(
    dealId,
    Boolean(vero1Active) && canViewInternal
  );

  // the row belongs to the SUBMITTED review governing the banner — a re-review
  // draft (Draft status) is not it, but NeedsReReview is still the governing one
  const governing =
    checklist?.status === Vero1ChecklistStatus.Submitted ||
    checklist?.status === Vero1ChecklistStatus.NeedsReReview;
  const prior = checklist?.originalOutcome;
  if (
    !vero1Active ||
    !canViewInternal ||
    !governing ||
    !checklist?.isOverride ||
    !prior ||
    // isOverride is structural (any non-agree submission); the row itself is
    // "amended" — same-verdict submissions have nothing to disclose
    prior === checklist.finalRecommendation
  ) {
    return null;
  }

  const openByDefault =
    checklist.finalRecommendation === Vero1Recommendation.Decline ||
    checklist.finalRecommendation === Vero1Recommendation.Cancel;
  const when = runDate(checklist.baseRunTimestamp);
  const whatChanged = Array.from(
    new Set(
      (checklist.items ?? [])
        .filter((item) => item.isEdited && item.overrideExplanation)
        .map((item) => item.overrideExplanation.trim())
    )
  ).join(" ");

  const row = (
    <Row
      open={openByDefault}
      $nested={!standalone}
      data-testid="vero1-lumen-amended-row"
    >
      <summary>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#B39BEC"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flex: "0 0 auto" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
        <InternalOnlyChip $tone="violet">Internal only</InternalOnlyChip>
        <SummaryText>
          Previous Lumen recommendation amended by VERO1
        </SummaryText>
        <ToggleHint>
          <span className="toggle-hide">Hide</span>
          <span className="toggle-show">Show</span>
        </ToggleHint>
      </summary>
      <Body>
        <div style={{ flex: "0 0 auto" }}>
          <ColLabel>Before VERO1 review</ColLabel>
          <PriorVerdict $color={recommendationVerdictColor(prior)}>
            {RECOMMENDATION_LABELS[prior]}
          </PriorVerdict>
          <PriorSource>{when ? `Lumen · ${when}` : "Lumen"}</PriorSource>
        </div>
        <Divider />
        <Changed>
          <ColLabel>What changed</ColLabel>
          {whatChanged && <ChangedBody>{whatChanged}</ChangedBody>}
          <AdverseNote>
            Adverse action notice is generated from the submitted VERO1
            recommendation, not from this prior.
          </AdverseNote>
        </Changed>
      </Body>
    </Row>
  );

  return standalone ? <Strip>{row}</Strip> : row;
};
